/**
 * Responsibility: the completed sales behind a card's prices - as a chart,
 * and as the list the chart is drawn from.
 *
 * Shared by both dialogs. They used to differ: discovery plotted BetterPop's
 * sales, which cover about three cards in four and none of the modern graded
 * market, while search plotted PriceCharting's, which go back months and
 * exist for both. Both now read the same source, so both use this.
 *
 * The average is drawn as a dashed line across the points because an average
 * is only trustworthy when the scatter around it is visible. A card whose
 * sales sit in a tight band has a price; one averaging the same figure from
 * a sale at double and a sale at half does not, and only the picture
 * separates them.
 *
 * Chart and table together rather than either alone. The chart answers
 * "which way is this going"; the table answers "is that one odd sale or
 * twenty", and links out so any point can be checked against the listing it
 * came from.
 */
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LineSeriesChart } from '@kit/components/charts/LineSeriesChart';
import { fmtCurrency } from '@kit/utils';
import type { SoldSale } from '../../api/client';
import { money, toNumber } from '../grading/money';
import { EM_DASH } from '../grading/CardFigures';

/**
 * Shorter than it was, to pay for the grade picker above it. The dialog is
 * a fixed height that must not scroll, so a row added above the chart has
 * to come out of the chart.
 */
const CHART_HEIGHT = 148;

/** Enough points to show a trend; past this the axis turns to mush. */
const MAX_POINTS = 60;

/** Rows in the list under the chart. The rest are a scroll away. */
const MAX_ROWS = 40;

type SalesHistoryProps = {
  sales: SoldSale[];
  loading?: boolean;
  /** Hides the list, for the tab that only wants the picture. */
  chartOnly?: boolean;
};

/**
 * Oldest first, which is the direction a chart reads.
 *
 * Sorted on the date rather than reversed. Reversing assumed the
 * catalogue returned its sold table strictly newest-first, and it does
 * not - the axis came out running 2026-08-14, 2024-05-31, 2025-02-09,
 * so the line was joining points that were not in time order and the
 * trend it drew was an artefact of the table's ordering.
 *
 * The cap takes the most recent points, not the first ones the table
 * happened to list. "The last sixty sales" is a claim worth making; "sixty
 * sales in whatever order they arrived" is not.
 */
function plottable(sales: SoldSale[]) {
  return sales
    .filter((sale) => sale.sold_on !== null && toNumber(sale.price) !== null)
    .sort((left, right) => (left.sold_on ?? '').localeCompare(right.sold_on ?? ''))
    .slice(-MAX_POINTS);
}

export function SalesChart({ sales, loading = false }: SalesHistoryProps) {
  const dated = plottable(sales);
  const prices = dated.map((sale) => toNumber(sale.price) as number);
  const average =
    prices.length > 0 ? prices.reduce((sum, price) => sum + price, 0) / prices.length : null;

  if (dated.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {loading ? 'Loading sales' : 'No dated sales to plot for this card.'}
      </Typography>
    );
  }

  const rows = dated.map((sale, index) => ({
    x: sale.sold_on ?? `sale ${index + 1}`,
    price: toNumber(sale.price),
    average,
  }));

  return (
    <LineSeriesChart
      rows={rows}
      xKey="x"
      height={CHART_HEIGHT}
      loading={loading}
      // Wrapped, not passed bare: Recharts calls a tick formatter as
      // (value, index) and fmtCurrency's second parameter is a currency
      // code, which threw "Invalid currency code: 4" and blanked the page.
      yFormat={(value: number) => fmtCurrency(value)}
      showLegend
      series={[
        { key: 'price', label: 'Sold for' },
        { key: 'average', label: 'Average', dashed: true },
      ]}
      emptyMessage="No completed sales for this card."
    />
  );
}

export function SalesTable({ sales }: { sales: SoldSale[] }) {
  if (sales.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No completed sales recorded for this card.
      </Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {sales.slice(0, MAX_ROWS).map((sale) => (
        <Stack
          key={sale.item_id}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            py: 0.6,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ width: 82, flexShrink: 0 }}>
            {sale.sold_on ?? EM_DASH}
          </Typography>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {sale.url ? (
              <Link
                href={sale.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                underline="hover"
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                noWrap
              >
                {sale.title}
              </Link>
            ) : (
              <Typography variant="body2" noWrap>
                {sale.title}
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {money(sale.price)}
          </Typography>
        </Stack>
      ))}

      {sales.length > MAX_ROWS ? (
        <Typography variant="caption" color="text.secondary" sx={{ pt: 1, display: 'block' }}>
          Showing the {MAX_ROWS} most recent of {sales.length}.
        </Typography>
      ) : null}
    </Box>
  );
}
